/**
 * Index membership sets — data, not algorithm (plan §0.3 Finding 2).
 * Demo source used alternate Nasdaq spellings; product code is NDX only.
 */
import type { Ticker } from '@meridian/contracts';
import { asTicker } from '@meridian/contracts';

const DJI_RAW =
  'AAPL MSFT NVDA AMZN JPM V UNH HD PG JNJ MRK CVX CAT GS MCD AXP BA IBM DIS VZ KO CSCO NKE WMT MMM HON CRM SHW AMGN';

const NDX_RAW =
  'AAPL MSFT NVDA AVGO AMD ADBE CSCO INTC QCOM TXN MU AMAT INTU PANW LRCX GOOGL META NFLX TMUS CMCSA EA WBD AMZN TSLA SBUX BKNG ABNB COST MDLZ KHC PEP PYPL AMGN GILD VRTX REGN ISRG ADP CSX MAR XEL EXC';

/**
 * A ReadonlySet with no runtime write path.
 *
 * `ReadonlySet<T>` is a compile-time view over a real `Set`, and `Object.freeze`
 * does not disable `Set.prototype.add`/`delete` — so a plain
 * `new Set()` exported as `ReadonlySet` still admits
 * `(DJI_SET as Set<string>).add('ZZZZ')`, after which `indexMembership('ZZZZ')`
 * reports Dow membership. These sets are barrel-exported, so that write path
 * reaches every consumer of the package.
 *
 * Returning a frozen facade that structurally satisfies `ReadonlySet` removes
 * the mutators entirely rather than merely hiding them from the type checker.
 */
function toTickerSet(raw: string): ReadonlySet<Ticker> {
  const inner = new Set(raw.split(' ').map((t) => asTicker(t)));
  const facade: ReadonlySet<Ticker> = Object.freeze({
    has: (value: Ticker): boolean => inner.has(value),
    keys: (): SetIterator<Ticker> => inner.keys(),
    values: (): SetIterator<Ticker> => inner.values(),
    entries: (): SetIterator<[Ticker, Ticker]> => inner.entries(),
    forEach: (fn: (v: Ticker, v2: Ticker, set: ReadonlySet<Ticker>) => void, thisArg?: unknown) => {
      inner.forEach((v, v2) => {
        fn.call(thisArg, v, v2, facade);
      });
    },
    get size(): number {
      return inner.size;
    },
    [Symbol.iterator]: (): SetIterator<Ticker> => inner[Symbol.iterator](),
  });
  return facade;
}

/** Dow Jones Industrial Average membership (29 names in the demo fixture). */
export const DJI_SET: ReadonlySet<Ticker> = toTickerSet(DJI_RAW);

/**
 * Nasdaq-100 membership for the synthetic fixture.
 * Demo source spelling is normalized; product exports NDX only.
 */
export const NDX_SET: ReadonlySet<Ticker> = toTickerSet(NDX_RAW);
